import { type EngineStateBase } from '@blaze-runtime/engine-data-access';
import { type CanvasElementId } from '@blaze-runtime/engine-domain';
import { type ConnectionId } from '@blaze-shared/api-contracts-domain';
import { isDefined } from '@blaze-shared/shared-util';

import { isElementType } from '../../../canvas-elements/element-model/element-type-guards';
import { getWizardSteps } from '../../../elements/wizard/wizard.utils';

/**
 * Finds the step-action wiring a questionnaire's steps no longer claim.
 *
 * A step's "next action" is a workflow the wizard runs when that step is left, wired by a
 * `next` connection from the wizard and named by the step's `nextAction.workflowId`. Delete the
 * step and the connection is unreachable: the step that named the workflow is gone, but the
 * wiring stays. This reads the steps as the desired state — the same way the rest of the
 * regeneration does — and reports whatever they leave over.
 *
 * **Dropping the connection is the whole job.** A workflow that loses its last connection is
 * removed by the next layout save, which diffs the saved connections against the current ones
 * (`getRemovedWorkflowIds`); one never saved as connected is swept on the backend. So nothing
 * here deletes a workflow, and the regeneration stays a single state commit.
 *
 * The wizard's own submit and status wiring is deliberately **not** swept. It belongs to the
 * wizard element rather than to any step, survives as long as the wizard does, and is what lets
 * `FormGroupSubmitWorkflowService` reuse an existing submit workflow instead of building a
 * second one once questions come back.
 */
export function collectOrphanedStepActionConnections(
  state: EngineStateBase,
  wizardId: CanvasElementId,
): ConnectionId[] {
  const wizard = state.entities[wizardId];
  if (!isElementType(wizard, 'wizard')) {
    throw new Error('element is not a wizard');
  }

  const claimedWorkflowIds = new Set(
    getWizardSteps(wizard.props)
      .map((step) => step.nextAction?.workflowId)
      .filter(isDefined),
  );

  return state.connections
    .filter(
      (connection) =>
        connection.eventType === 'next' &&
        connection.source.layerType === 'Ui' &&
        connection.source.id === wizardId &&
        connection.target.layerType === 'Workflow' &&
        !claimedWorkflowIds.has(connection.target.id),
    )
    .map(({ id }) => id);
}
